/**
 * Terminal mode — a pure, instant, client-side command interpreter over the
 * content registries. No API calls, no side effects: commands return lines
 * plus optional effects/actions that the palette executes.
 *
 * buildLaboratoryState() is a server-safe pure function over the registries,
 * so importing it here is fine. Reading `useWorld`'s current state
 * (agents/freshness/runId/epoch) is likewise fine — it is a plain client-side
 * store read, not a network call, and stays consistent with this file's own
 * "no API calls, no side effects" contract: `agents`/`system status`/
 * `inspect core` render whatever the store already holds, they never fetch
 * anything themselves.
 *
 * Localization: command KEYWORDS stay English (help, list research, …) so the
 * interpreter is stable across locales; the human-readable output is translated
 * via the `lang` parameter, and registry data comes from the localized
 * resolvers in content/i18n so titles/labels match the active locale.
 */

import {
  getProjects,
  getResearch,
  localizeAgents,
  getKnowledgeNodes,
  getPublications,
} from '../content/i18n.ts';
import { knowledgeEdges } from '../content/knowledge.ts';
import { buildLaboratoryState } from '../lab/state.ts';
import { ZONES } from '../lab/types.ts';
import { useWorld } from '../lab/store.ts';
import { displayFreshness } from '../lab/freshness-label.ts';
import {
  agentsHeaderLine,
  statusAgentsLabel,
  statusAgentsValue,
  coreNoteLine,
} from '../lab/terminal-freshness-labels.ts';
import type { Lang } from '../i18n/config';
import type { UIAction } from './actions';

export interface TermLine {
  text: string;
  tone?: 'default' | 'accent' | 'dim' | 'error';
}

export interface TermResult {
  lines: TermLine[];
  effect?: 'clear' | 'exit';
  action?: UIAction;
}

const t = (text: string, tone?: TermLine['tone']): TermLine => ({ text, tone });

// ── Localized labels ────────────────────────────────────────────────────────
// Keywords in the output (help, open <slug>, /knowledge, [SIM], zone ids, kind
// ids, status enums) stay in Latin — they are part of the machine contract, not
// prose. Everything a human reads as a sentence is translated.

interface TermLabels {
  bannerTitle: string;
  bannerHelp: string;
  commands: string;
  researchDirs: string; // header word, used as `${researchDirs} (n):`
  projectsWord: string;
  publicationsWord: string;
  openWith: string;
  nodes: string; // graph header noun
  edges: string; // graph header noun
  graphHead: string; // 'knowledge graph:' label
  kinds: string;
  domainsLabel: string;
  graphView: string;
  stateHead: string;
  fStatus: string;
  fProjects: string;
  fResearch: string;
  fNodes: string;
  fPublications: string;
  fRun: string;
  fEpoch: string;
  fUpdated: string;
  coreTitle: string;
  coreZones: string; // 'zones:' label
  openUsage: string;
  openUsageHint: string;
  openingProject: string; // verb, `${openingProject} ${name} → …`
  openingResearch: string;
  unknownSlug: string; // `${unknownSlug} ${slug}`
  didYouMean: string; // label, no trailing space
  slugTry: string;
  unknownCommand: string; // `${unknownCommand} ${cmd}`
  helpTry: string;
  leaving: string;
  // sentence builders (interpolate localized numbers)
  coreBody: (p: number, r: number, k: number, pub: number) => string;
}

const LABELS: Record<Lang, TermLabels> = {
  en: {
    bannerTitle: 'LAB terminal — registry access, local, instant.',
    bannerHelp: "type 'help' for commands · 'exit' to return",
    commands: 'commands:',
    researchDirs: 'research directions',
    projectsWord: 'projects',
    publicationsWord: 'publications',
    openWith: 'open one with: open <slug>',
    nodes: 'nodes',
    edges: 'edges',
    graphHead: 'knowledge graph:',
    kinds: 'kinds:',
    domainsLabel: 'domains:',
    graphView: 'view it at /knowledge',
    stateHead: 'laboratory state (real, from content registries):',
    fStatus: 'status',
    fProjects: 'projects',
    fResearch: 'research directions',
    fNodes: 'knowledge nodes',
    fPublications: 'publications',
    fRun: 'run',
    fEpoch: 'epoch',
    fUpdated: 'updated',
    coreTitle: 'CORE — the laboratory nucleus.',
    coreZones: 'zones:',
    openUsage: 'usage: open <slug>',
    openUsageHint: "try 'show projects' or 'list research' for slugs",
    openingProject: 'opening project',
    openingResearch: 'opening research',
    unknownSlug: 'unknown slug:',
    didYouMean: 'did you mean:',
    slugTry: "try 'show projects' or 'list research'",
    unknownCommand: 'unknown command:',
    helpTry: "type 'help' for the command list",
    leaving: 'leaving terminal.',
    coreBody: (p, r, k, pub) =>
      `StudyLabPro, an independent AI-first R&D laboratory: ${p} projects, ${r} research directions, ${k} knowledge nodes, ${pub} publications.`,
  },
  ru: {
    bannerTitle: 'Терминал LAB — доступ к реестрам, локально, мгновенно.',
    bannerHelp: 'введите «help» для списка команд · «exit» — выход',
    commands: 'команды:',
    researchDirs: 'направления исследований',
    projectsWord: 'проекты',
    publicationsWord: 'публикации',
    openWith: 'открыть: open <slug>',
    nodes: 'узлов',
    edges: 'рёбер',
    graphHead: 'граф знаний:',
    kinds: 'типы:',
    domainsLabel: 'области:',
    graphView: 'смотреть на /knowledge',
    stateHead: 'состояние лаборатории (реально, из контентных реестров):',
    fStatus: 'статус',
    fProjects: 'проекты',
    fResearch: 'направления исследований',
    fNodes: 'узлы знаний',
    fPublications: 'публикации',
    fRun: 'запуск',
    fEpoch: 'эпоха',
    fUpdated: 'обновлено',
    coreTitle: 'CORE — ядро лаборатории.',
    coreZones: 'зоны:',
    openUsage: 'использование: open <slug>',
    openUsageHint: 'слаги смотрите в «show projects» или «list research»',
    openingProject: 'открываю проект',
    openingResearch: 'открываю исследование',
    unknownSlug: 'неизвестный слаг:',
    didYouMean: 'возможно, вы имели в виду:',
    slugTry: 'попробуйте «show projects» или «list research»',
    unknownCommand: 'неизвестная команда:',
    helpTry: 'введите «help» для списка команд',
    leaving: 'выход из терминала.',
    coreBody: (p, r, k, pub) =>
      `StudyLabPro — независимая AI-first R&D-лаборатория: ${p} проектов, ${r} направлений исследований, ${k} узлов знаний, ${pub} публикаций.`,
  },
};

export function TERMINAL_BANNER(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  return [t(L.bannerTitle, 'accent'), t(L.bannerHelp, 'dim')];
}

// Command keywords are the stable machine contract — never translated.
const COMMANDS = [
  'help',
  'list research',
  'show projects',
  'agents',
  'knowledge graph',
  'system status',
  'inspect core',
  'publications',
  'open <slug>',
  'clear',
  'exit',
];

function pad(s: string, w: number): string {
  return s.length >= w ? s : s + ' '.repeat(w - s.length);
}

function helpLines(lang: Lang): TermLine[] {
  return [t(LABELS[lang].commands, 'dim'), ...COMMANDS.map((c) => t(`  ${c}`))];
}

function researchLines(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const research = getResearch(lang);
  const w = Math.max(...research.map((r) => r.slug.length)) + 2;
  return [
    t(`${L.researchDirs} (${research.length}):`, 'dim'),
    ...research.map((r) => t(`  ${pad(r.slug, w)}${r.title} — ${r.stateLabel}`)),
    t(L.openWith, 'dim'),
  ];
}

function projectLines(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const projects = getProjects(lang);
  const w = Math.max(...projects.map((p) => p.slug.length)) + 2;
  return [
    t(`${L.projectsWord} (${projects.length}):`, 'dim'),
    ...projects.map((p) => t(`  ${pad(p.slug, w)}${p.name} — ${p.statusLabel}`)),
    t(L.openWith, 'dim'),
  ];
}

function agentLines(lang: Lang): TermLine[] {
  const { agents: liveAgents, rosterLive, freshness, liveMode } = useWorld.getState();
  const display = displayFreshness(rosterLive, freshness);
  // localizeAgents is backward-compatible with the static fallback registry
  // (full per-id role/task overlay) and also handles Genesis-Live ids
  // (`N0001…`, unknown ahead of time) by translating what the engine itself
  // reported — see agent-localize.ts.
  const agents = localizeAgents(liveAgents, lang);
  // Column widths are measured, never assumed: a fixed 24-column role field
  // was narrower than the widest Russian role and ran the columns together
  // (`Исследовательский интеллектactive`). Both locales now lay out from the
  // widest real value on screen.
  const nameW = Math.max(...agents.map((a) => a.name.length), 4) + 2;
  const roleW = Math.max(...agents.map((a) => a.role.length), 4) + 2;
  const statusW = Math.max(...agents.map((a) => a.status.length), 4) + 2;
  return [
    t(agentsHeaderLine(lang, display, liveMode), 'accent'),
    ...agents.map((a) =>
      t(`  ${pad(a.name, nameW)}${pad(a.role, roleW)}${pad(a.status, statusW)}${a.task}`)
    ),
  ];
}

function knowledgeLines(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const nodes = getKnowledgeNodes(lang);
  const domains = nodes.filter((n) => n.kind === 'domain');
  const byKind = nodes.reduce<Record<string, number>>((acc, n) => {
    acc[n.kind] = (acc[n.kind] ?? 0) + 1;
    return acc;
  }, {});
  return [
    t(`${L.graphHead} ${nodes.length} ${L.nodes} · ${knowledgeEdges.length} ${L.edges}`, 'dim'),
    t(
      `  ${L.kinds} ${Object.entries(byKind)
        .map(([k, n]) => `${k}=${n}`)
        .join(' · ')}`
    ),
    t(`  ${L.domainsLabel} ${domains.map((d) => d.label).join(', ')}`),
    t(L.graphView, 'dim'),
  ];
}

function statusLines(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const st = buildLaboratoryState();
  const { agents: liveAgents, rosterLive, freshness, runId, epoch, liveMode, liveStats } =
    useWorld.getState();
  const display = displayFreshness(rosterLive, freshness);
  // "active agents 10" over a roster of 3 acting and 7 idle agents was a
  // false statement — the whole roster was being labelled active. The row
  // now reports the same two numbers `/agents` does (acting rows / rows on
  // hand) and, when the bridge says the universe is larger than the roster
  // it served, the real population beside them.
  // Both numbers describe the roster actually on screen — the static
  // fallback before a live one lands, the observed one afterwards — so the
  // row can never describe a population the visitor is not looking at.
  const acting = liveAgents.filter((a) => a.status !== 'idle').length;
  const shown = liveAgents.length;
  const rows: [string, string | number][] = [
    [L.fStatus, st.status],
    [L.fProjects, st.projects],
    [L.fResearch, st.researchDirections],
    [L.fNodes, st.knowledgeNodes],
    [L.fPublications, st.publications],
    [
      statusAgentsLabel(lang, display),
      statusAgentsValue(
        lang,
        display,
        {
          acting,
          shown,
          population: display === 'OFFLINE' ? null : liveStats?.activeAgents ?? null,
        },
        liveMode,
      ),
    ],
    ...(runId !== null ? [[L.fRun, runId] as [string, string]] : []),
    ...(epoch !== null ? [[L.fEpoch, epoch] as [string, number]] : []),
    [L.fUpdated, st.updatedAt],
  ];
  const w = Math.max(...rows.map(([label]) => label.length)) + 2;
  return [
    t(L.stateHead, 'dim'),
    ...rows.map(([label, value]) => t(`  ${pad(label, w)}${value}`)),
  ];
}

function coreLines(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const st = buildLaboratoryState();
  const { rosterLive, freshness, liveMode } = useWorld.getState();
  const display = displayFreshness(rosterLive, freshness);
  return [
    t(L.coreTitle, 'accent'),
    t(
      `  ${L.coreBody(st.projects, st.researchDirections, st.knowledgeNodes, st.publications)}`
    ),
    t(`  ${L.coreZones} ${ZONES.join(' · ')}`),
    t(`  ${coreNoteLine(lang, display, liveMode)}`, 'dim'),
  ];
}

function publicationLines(lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const pubs = getPublications(lang);
  return [
    t(`${L.publicationsWord} (${pubs.length}):`, 'dim'),
    ...pubs.map((p) => t(`  ${p.title} — ${p.statusLabel}`)),
  ];
}

function openCommand(arg: string, lang: Lang): TermResult {
  const L = LABELS[lang];
  const slug = arg.trim().toLowerCase();
  if (!slug) {
    return { lines: [t(L.openUsage, 'dim'), t(L.openUsageHint, 'dim')] };
  }
  const projects = getProjects(lang);
  const research = getResearch(lang);
  const p = projects.find((x) => x.slug === slug);
  if (p) {
    return {
      lines: [t(`${L.openingProject} ${p.name} → /projects/${p.slug}`, 'accent')],
      action: { type: 'OPEN_PROJECT', slug: p.slug },
    };
  }
  const r = research.find((x) => x.slug === slug);
  if (r) {
    return {
      lines: [t(`${L.openingResearch} ${r.title} → /research/${r.slug}`, 'accent')],
      action: { type: 'OPEN_RESEARCH', slug: r.slug },
    };
  }
  const near = [...projects.map((x) => x.slug), ...research.map((x) => x.slug)].filter(
    (s) => s.includes(slug) || slug.includes(s)
  );
  return {
    lines: [
      t(`${L.unknownSlug} ${slug}`, 'error'),
      near.length
        ? t(`  ${L.didYouMean} ${near.join(', ')}`, 'dim')
        : t(`  ${L.slugTry}`, 'dim'),
    ],
  };
}

function suggest(cmd: string, lang: Lang): TermLine[] {
  const L = LABELS[lang];
  const first = cmd.split(/\s+/)[0];
  const near = COMMANDS.filter((c) => c.startsWith(first) || c.includes(first));
  return [
    t(`${L.unknownCommand} ${cmd}`, 'error'),
    near.length
      ? t(`  ${L.didYouMean} ${near.join(' · ')}`, 'dim')
      : t(`  ${L.helpTry}`, 'dim'),
  ];
}

export function runTerminalCommand(input: string, lang: Lang = 'en'): TermResult {
  const cmd = input.trim().replace(/\s+/g, ' ');
  const lower = cmd.toLowerCase();

  switch (lower) {
    case 'help':
    case '?':
      return { lines: helpLines(lang) };
    case 'list research':
    case 'research':
      return { lines: researchLines(lang) };
    case 'show projects':
    case 'projects':
      return { lines: projectLines(lang) };
    case 'agents':
      return { lines: agentLines(lang) };
    case 'knowledge graph':
    case 'knowledge':
    case 'graph':
      return { lines: knowledgeLines(lang) };
    case 'system status':
    case 'status':
      return { lines: statusLines(lang) };
    case 'inspect core':
    case 'core':
      return { lines: coreLines(lang) };
    case 'publications':
      return { lines: publicationLines(lang) };
    case 'clear':
      return { lines: [], effect: 'clear' };
    case 'exit':
    case 'quit':
      return { lines: [t(LABELS[lang].leaving, 'dim')], effect: 'exit' };
  }

  if (lower.startsWith('open ') || lower === 'open') {
    return openCommand(cmd.slice(4), lang);
  }

  return { lines: suggest(cmd, lang) };
}
